import React from 'react'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import AnalyticsCard from './analytics-card'
import useStyles from '../styles'
import {
  formatMonthLabel,
  getMonthInsight,
  getMonthOverMonth,
  fitToMonths,
  sortByMonth,
} from '../utils'

const ChartTooltip = ({ active, payload, label, valueFormatter }) => {
  const classes = useStyles()

  if (!active || !payload?.length) return null

  return (
    <Box className={classes.chartTooltip}>
      <Typography component="div" className={classes.chartTooltipLabel}>
        {formatMonthLabel(label)}
      </Typography>
      <Typography component="div" className={classes.chartTooltipValue}>
        {valueFormatter(payload[0].value)}
      </Typography>
    </Box>
  )
}

const MonthlyTrendChart = ({
  id,
  title,
  subtitle,
  data,
  dataKey,
  months,
  color,
  valueFormatter,
  axisFormatter,
  summaryFormatter,
  insightFormatter,
  insightSubject,
  emptyMessage,
  errorMessage,
  loading,
  error,
  className,
}) => {
  const classes = useStyles()
  const gradientId = `${id}Fill`
  const sortedData = months
    ? fitToMonths(data, months, dataKey)
    : sortByMonth(data)
  const isEmpty = months
    ? !data?.some(point => months.includes(point.month))
    : !data?.length
  const monthOverMonth = getMonthOverMonth(sortedData, dataKey)
  const showSummary = summaryFormatter && !loading && !error && !isEmpty
  const summary = showSummary ? (
    <Typography className={classes.cardSummaryValue}>
      {summaryFormatter(monthOverMonth.current)}
    </Typography>
  ) : null
  const insight = insightSubject
    ? getMonthInsight(monthOverMonth, {
      subject: insightSubject,
      format: insightFormatter || summaryFormatter,
    })
    : null

  return (
    <AnalyticsCard
      title={title}
      subtitle={subtitle}
      summary={summary}
      insight={insight}
      className={className}
      loading={loading}
      error={error}
      errorMessage={errorMessage}
      empty={isEmpty}
      emptyMessage={emptyMessage}
    >
      <Box className={classes.chartArea}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={sortedData}
            margin={{ top: 4, right: 0, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.25} />
                <stop offset="95%" stopColor={color} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="month"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12 }}
              tickFormatter={month => formatMonthLabel(month, 'LLL ’yy')}
              minTickGap={24}
              padding={{ right: 16 }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12 }}
              width={48}
              allowDecimals={false}
              tickFormatter={axisFormatter}
            />
            <Tooltip
              content={<ChartTooltip valueFormatter={valueFormatter} />}
              cursor={{
                stroke: 'rgba(0, 32, 91, 0.12)',
                strokeWidth: 1,
                fill: 'rgba(0, 32, 91, 0.04)',
              }}
            />
            <Area
              type="monotone"
              dataKey={dataKey}
              stroke={color}
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              dot={{ fill: color, stroke: color, fillOpacity: 0.6, r: 3 }}
              activeDot={{ r: 5, fill: color, stroke: color }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </AnalyticsCard>
  )
}

export default MonthlyTrendChart
